import type { IdentityV1 } from '@alpod-ai-writer/backend';

/**
 * Privilege keys that grant access to the admin UI. New admin resources must
 * be added here explicitly — don't iterate `Object.values(systemPrivileges)`,
 * since the type allows arbitrary keys and would grant access on any extra
 * privilege the backend happens to return.
 */
export const ADMIN_PRIVILEGE_KEYS = ['Identities', 'Documents', 'Templates', 'JobQueue', 'AIUsage'] as const;
export type AdminPrivilegeKey = typeof ADMIN_PRIVILEGE_KEYS[number];

type PrivilegeMap = Record<string, string | null | undefined>;

function getPrivilege(identity: IdentityV1.OwnRecord, key: AdminPrivilegeKey): string | null {
    const sp = identity.systemPrivileges as PrivilegeMap | null | undefined;
    return sp?.[key] ?? null;
}

export function hasAdminAccess(identity: IdentityV1.OwnRecord): boolean {
    return ADMIN_PRIVILEGE_KEYS.some((key) => {
        const value = getPrivilege(identity, key);
        return value === 'Read' || value === 'Manage';
    });
}

export function canRead(identity: IdentityV1.OwnRecord, key: AdminPrivilegeKey): boolean {
    const value = getPrivilege(identity, key);
    return value === 'Read' || value === 'Manage';
}

export function canManage(identity: IdentityV1.OwnRecord, key: AdminPrivilegeKey): boolean {
    return getPrivilege(identity, key) === 'Manage';
}
